const ADMIN_ROLES = [
  "super_admin",
  "reseller",
  "solution_provider",
  "system_admin",
];
const ALL_FILTER_VALUE = "all";

export const normalizeOrgId = (value) => {
  const raw =
    value && typeof value === "object" ? value.id || value._id || value : value;
  const id = raw?.toString?.().trim?.() || "";
  return id && id !== "[object Object]" ? id : "";
};

const normalizeName = (value) =>
  typeof value === "string" ? value.trim() : "";
const normalizeUnitLevel = (value) =>
  typeof value === "string" ? value.trim() : "";

export const isSuperAdminRole = (role) =>
  ADMIN_ROLES.includes((role || "").toString().trim().toLowerCase());

// Used by surfaces that need to gate "company-wide" data (General-category
// disclosures, parent-level entries) — admins and users explicitly granted
// parent-company scope can see it; BU-only users cannot.
export const hasParentAccess = (user) =>
  isSuperAdminRole(user?.role) || Boolean(user?.assignedToParentCompany);

export const isAllFilterValue = (value) =>
  (value || "").toString().trim().toLowerCase() === ALL_FILTER_VALUE;

const uniqueById = (items = []) => {
  const seen = new Set();
  const normalized = [];

  items.forEach((item) => {
    const id = normalizeOrgId(item?.id || item);
    if (!id || seen.has(id)) return;
    const source = item && typeof item === "object" ? item : {};

    seen.add(id);
    normalized.push({
      id,
      name: normalizeName(source.name) || id,
      unitLevel: normalizeUnitLevel(source.unitLevel || source.unit_level),
      code: normalizeName(source.code),
      level1Code: normalizeName(source.level1Code || source.level_1_code),
      parentName: normalizeName(source.parentName || source.parent_name),
      pathLabel: normalizeName(source.pathLabel || source.path_label),
      country: normalizeName(source.country),
      countryLabel: normalizeName(source.countryLabel || source.country_label),
      locationLabel: normalizeName(
        source.locationLabel || source.location_label,
      ),
    });
  });

  return normalized;
};

export const normalizeAssignedOrgs = (organizationUnitId) => {
  if (!organizationUnitId) return [];
  const source = Array.isArray(organizationUnitId)
    ? organizationUnitId
    : [organizationUnitId];

  return uniqueById(source);
};

export const flattenOrgTreeForFlatView = (tree = []) => {
  const collected = [];
  const visit = (nodes = [], context = { pathNames: [], level1Code: "" }) => {
    nodes.forEach((node) => {
      const isGroup = Boolean(node?.isGroup);
      const id = normalizeOrgId(node?.id);
      const name = normalizeName(node?.name) || id;
      const unitLevel = normalizeUnitLevel(node?.unitLevel || node?.unit_level);
      const code = normalizeName(node?.code);
      const nextLevel1Code =
        unitLevel === "level_1" && code ? code : context.level1Code;
      const parentName = context.pathNames[context.pathNames.length - 1] || "";
      const pathLabel = context.pathNames.join(" > ");

      if (!isGroup && id) {
        collected.push({
          id,
          name,
          unitLevel,
          code,
          level1Code: nextLevel1Code,
          parentName,
          pathLabel,
          country: normalizeName(node?.country),
          countryLabel: normalizeName(
            node?.countryLabel || node?.country_label,
          ),
          locationLabel: normalizeName(
            node?.locationLabel || node?.location_label,
          ),
        });
      }

      const nextPathNames = isGroup
        ? context.pathNames
        : [...context.pathNames, name];
      visit(node?.children || [], {
        pathNames: nextPathNames,
        level1Code: nextLevel1Code,
      });
    });
  };

  visit(tree);

  return uniqueById(collected);
};

export const normalizeOrgIds = (orgIds = []) => {
  if (!Array.isArray(orgIds)) return [];
  return uniqueById(orgIds)
    .map((item) => item.id)
    .sort((a, b) => a.localeCompare(b));
};

export const readOrgCount = (countById, orgId) => {
  const key = normalizeOrgId(orgId).toLowerCase();
  if (!key || !countById) return 0;

  const value =
    typeof countById.get === "function" ? countById.get(key) : countById[key];
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};
